<template>
  <div class="auth-form w-full">
    <a-form
      ref="formRef"
      :model="model"
      :rules="rules"
      :layout="layout"
      :class="['auth-form__body', tone === 'login' ? 'auth-form__body--login' : 'auth-form__body--app']"
      @finish="handleFinish"
    >
      <slot></slot>
    </a-form>

    <div v-if="submitText" class="auth-form__footer mt-4">
      <a-button
        type="primary"
        size="large"
        block
        :loading="loading"
        @click="submit"
      >
        {{ submitText }}
      </a-button>
    </div>

    <slot name="extra"></slot>
  </div>
</template>

<script>
export default {
  name: 'AuthForm',
  props: {
    model: {
      type: Object,
      required: true
    },
    rules: {
      type: Object,
      default: () => ({})
    },
    layout: {
      type: String,
      default: 'vertical'
    },
    submitText: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    // 表单风格：login（登录页暗色），app（全站跟随 dark 模式）
    tone: {
      type: String,
      default: 'app',
      validator: (v) => ['app', 'login'].includes(v)
    }
  },
  methods: {
    submit () {
      this.validate()
        .then(() => {
          this.$emit('submit')
        })
        .catch(() => {})
    },
    handleFinish () {
      this.$emit('submit')
    },
    validate () {
      return this.$refs.formRef.validate()
    },
    validateFields (fields) {
      return this.$refs.formRef.validateFields(fields)
    },
    resetFields () {
      return this.$refs.formRef.resetFields()
    },
    clearValidate (fields) {
      return this.$refs.formRef.clearValidate(fields)
    }
  }
}
</script>

<style scoped>
.auth-form__body :deep(.ant-form-item) {
  @apply mb-0;
}

/* AuthForm 清零了 ant-form-item 默认 mb，相邻表单项在此补间距（登录页等） */
.auth-form__body :deep(.ant-form-item + .ant-form-item) {
  @apply mt-5;
}

.auth-form__body :deep(.ant-form-item-explain-error) {
  @apply mt-1 text-xs text-red-500 dark:text-red-400;
}

.auth-form__body :deep(.ant-input-affix-wrapper > input.ant-input) {
  @apply bg-transparent;
}

.auth-form__body--login :deep(.ant-input-affix-wrapper input:-webkit-autofill),
.auth-form__body--login :deep(.ant-input-affix-wrapper input:-webkit-autofill:hover),
.auth-form__body--login :deep(.ant-input-affix-wrapper input:-webkit-autofill:focus),
.auth-form__body--login :deep(.ant-input-affix-wrapper input:-webkit-autofill:active) {
  -webkit-text-fill-color: #ffffff !important;
  transition: background-color 9999s ease-in-out 0s;
  box-shadow: 0 0 0px 1000px transparent inset !important;
}

.auth-form__body :deep(.ant-input-group) {
  @apply flex;
}

.auth-form__body :deep(.ant-input-group .ant-input) {
  @apply flex-1;
}

.auth-form__body :deep(.ant-input-group .ant-input-lg) {
  @apply h-11 leading-[44px];
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.auth-form__body :deep(.ant-input-group .ant-btn) {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.auth-form__body :deep(.ant-input-group .ant-btn-lg) {
  @apply h-11 leading-[44px] py-0;
}

/* tone = login（暗色登录页） */
.auth-form__body--login :deep(.ant-input-affix-wrapper),
.auth-form__body--login :deep(.ant-input),
.auth-form__body--login :deep(.ant-input-password .ant-input) {
  @apply bg-white/5 text-white border-white/10;
}
.auth-form__body--login :deep(.ant-input-affix-wrapper:hover),
.auth-form__body--login :deep(.ant-input:hover),
.auth-form__body--login :deep(.ant-input-password .ant-input:hover) {
  @apply border-white/20;
}
.auth-form__body--login :deep(.ant-input-affix-wrapper-focused),
.auth-form__body--login :deep(.ant-input:focus),
.auth-form__body--login :deep(.ant-input-password .ant-input:focus) {
  @apply border-white/30;
}
.auth-form__body--login :deep(.ant-input-affix-wrapper input::placeholder),
.auth-form__body--login :deep(.ant-input::placeholder),
.auth-form__body--login :deep(.ant-input-password .ant-input::placeholder) {
  @apply text-white/50;
}
.auth-form__body--login :deep(.ant-input-group .ant-btn) {
  @apply bg-white/10 border-white/10 text-white;
}
.auth-form__body--login :deep(.ant-input-group .ant-btn:hover:not(:disabled)) {
  @apply bg-white/15 border-white/20 text-white;
}
.auth-form__body--login :deep(.ant-input-group .ant-btn:disabled) {
  @apply bg-white/5 border-white/5 text-white/30;
}

/* tone = app（全站跟随 dark 模式） */
.auth-form__body--app :deep(.ant-input-affix-wrapper),
.auth-form__body--app :deep(.ant-input),
.auth-form__body--app :deep(.ant-input-password .ant-input) {
  @apply bg-white text-slate-900 border-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700;
}
.auth-form__body--app :deep(.ant-input-affix-wrapper:hover),
.auth-form__body--app :deep(.ant-input:hover),
.auth-form__body--app :deep(.ant-input-password .ant-input:hover) {
  @apply border-slate-300 dark:border-slate-600;
}
.auth-form__body--app :deep(.ant-input-affix-wrapper-focused),
.auth-form__body--app :deep(.ant-input:focus),
.auth-form__body--app :deep(.ant-input-password .ant-input:focus) {
  @apply border-primary;
}
.auth-form__body--app :deep(.ant-input-affix-wrapper input::placeholder),
.auth-form__body--app :deep(.ant-input::placeholder),
.auth-form__body--app :deep(.ant-input-password .ant-input::placeholder) {
  @apply text-slate-400;
}
.auth-form__body--app :deep(.ant-input-group .ant-btn) {
  @apply bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100;
}
.auth-form__body--app :deep(.ant-input-group .ant-btn:hover:not(:disabled)) {
  @apply bg-slate-200 border-slate-300 dark:bg-slate-700 dark:border-slate-600;
}
.auth-form__body--app :deep(.ant-input-group .ant-btn:disabled) {
  @apply bg-slate-100 border-slate-200 text-slate-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500;
}
</style>
